import type {
  CanonicalColumnId,
  EventSettableColumnId,
} from '@features/event-level-column-rename/types';

/**
 * The key the legacy event-configuration dropdown reports when the Manage Column Display Names
 * option is selected. Shared between the menu item and the dropdown's `onSelect` switch so a
 * typo in either half cannot silently unwire the option. With the Line Item Settings drawer on,
 * the entry is a row inside that drawer instead (no menu key involved).
 */
export const COLUMN_LABELS_MENU_ITEM_KEY = 'column_labels';

/**
 * Every canonical column id, in the order the product spec lists them. The order is the one
 * the rename drawer renders rows in, so keep it stable rather than alphabetising it.
 */
export const CANONICAL_COLUMN_IDS: readonly CanonicalColumnId[] = [
  'lineItemName',
  'pricePerUnit',
  'suggestedPricePerUnit',
  'unitOfMeasure',
  'quantityOfUnits',
  'itemsPerUnit',
  'quantityOfItems',
  'discountPerUnit',
  'lineItemPrice',
  'quotedQuantityOfUnits',
  'totalAdditionalCosts',
  'totalAllInPrice',
  'negotiationPricePerUnit',
];

/**
 * The 12 columns the event-level write endpoint accepts — every canonical id except
 * `negotiationPricePerUnit`, which is renameable organisation-wide only.
 */
export const EVENT_SETTABLE_COLUMN_IDS: readonly EventSettableColumnId[] = CANONICAL_COLUMN_IDS
  .filter((columnId): columnId is EventSettableColumnId => columnId !== 'negotiationPricePerUnit');

/**
 * Each canonical id as a named constant. Paint points import these instead of re-declaring
 * local string literals, so every surface spells a column's id from the same place.
 */
export const CANONICAL_COLUMN_ID = {
  discountPerUnit: 'discountPerUnit',
  itemsPerUnit: 'itemsPerUnit',
  lineItemName: 'lineItemName',
  lineItemPrice: 'lineItemPrice',
  negotiationPricePerUnit: 'negotiationPricePerUnit',
  pricePerUnit: 'pricePerUnit',
  quantityOfItems: 'quantityOfItems',
  quantityOfUnits: 'quantityOfUnits',
  quotedQuantityOfUnits: 'quotedQuantityOfUnits',
  suggestedPricePerUnit: 'suggestedPricePerUnit',
  totalAdditionalCosts: 'totalAdditionalCosts',
  totalAllInPrice: 'totalAllInPrice',
  unitOfMeasure: 'unitOfMeasure',
} as const satisfies Record<CanonicalColumnId, CanonicalColumnId>;

/**
 * Canonical id to the i18n key this client prints when no override applies.
 *
 * These are the *client's* keys. Three columns are filed under a different key here than in
 * the Rails registry — Quantity of Units, Items Per Unit and Quantity of Items — which is
 * exactly why labels travel between the repos as canonical ids instead of i18n keys.
 */
export const CLIENT_I18N_KEY: Record<CanonicalColumnId, string> = {
  discountPerUnit: 'general.discount_per_unit',
  itemsPerUnit: 'line_items.items_per_unit',
  lineItemName: 'general.line_item_name',
  lineItemPrice: 'line_items.line_item_price',
  negotiationPricePerUnit: 'line_items.negotiation_price_per_unit',
  pricePerUnit: 'general.price_per_unit',
  quantityOfItems: 'line_items.quantity_of_items',
  quantityOfUnits: 'line_items.quantity_of_units',
  quotedQuantityOfUnits: 'line_items.quoted_quantity_of_units',
  suggestedPricePerUnit: 'line_items.suggested_price_per_unit',
  totalAdditionalCosts: 'bid_request.additional_fees',
  totalAllInPrice: 'bid_request.total_all_in_price',
  unitOfMeasure: 'general.uom',
};

/**
 * Buyer line-items grid column key to canonical id.
 *
 * Only columns whose identity is provable from the grid's own configuration are listed: each
 * entry corresponds to a `columnNameOverrides[...]` lookup in `buyerInitialColumnSettings`,
 * where the organisation-level override key already names the column, or to a default i18n key
 * that is exactly the column's `CLIENT_I18N_KEY`. A guessed entry compiles, passes its own tests
 * and renames the wrong column, so do not extend this by inference — read the grid config for
 * the surface you are wiring and add only what it proves.
 *
 * - `discount`: `columnNameOverrides['general.discount_per_unit']` — `discountPerUnit`.
 * - `supplier_quantity`: default `t('line_items.quoted_quantity_of_units')` = `CLIENT_I18N_KEY.quotedQuantityOfUnits` — `quotedQuantityOfUnits`.
 */
export const BUYER_GRID_COLUMN_CANONICAL_IDS = {
  baseline_price: 'lineItemPrice',
  discount: 'discountPerUnit',
  extended_quantity: 'quantityOfUnits',
  name: 'lineItemName',
  price: 'pricePerUnit',
  supplier_quantity: 'quotedQuantityOfUnits',
  units: 'unitOfMeasure',
} as const satisfies Record<string, CanonicalColumnId>;

/**
 * The comparison the backend's label validation uses — case- and whitespace-insensitive — so the
 * download and upload collision guards agree with the write path about what "the same name" means.
 */
export const normalizeLabel = (label: string): string => label.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Change Log drawer attribute key to canonical id. `price` is the buyer grid's baseline
 * price-per-unit column (same default label as `BUYER_GRID_COLUMN_CANONICAL_IDS.price`), so it
 * takes that column's rename. `negotiation_starting_price` is deliberately absent: that column
 * is renameable organization-wide only, never per event.
 */
export const CHANGE_LOG_ATTRIBUTE_CANONICAL_IDS: Partial<Record<string, CanonicalColumnId>> = {
  price: 'pricePerUnit',
};
